// Competitor parody-archetype sprites. Original mascots that evoke each
// Cloudflare competitor via palette + emblem + punny name — no real logos or
// marks (legally defensible per Adam's "parody archetypes" choice). Frames are
// baked once to offscreen canvases at first render (DOM is never touched from
// the game constructor, so the class instantiates under the test runner).
//
// This is placeholder-grade procedural art behind a stable interface; the
// high-fidelity baker will later emit the same ActorSprites (with 8 view
// angles) and drop in here unchanged.

import type { SpriteFrame } from "./types";

export interface ActorSprites {
  readonly walk: readonly SpriteFrame[];
  readonly pain: SpriteFrame;
  readonly die: readonly SpriteFrame[];
}

export type Emblem = "ring" | "wedge" | "bars" | "bridge";

export interface ArchetypeSpec {
  readonly kind: string;
  /** Parody name shown in the kill feed / HUD (evokes, never copies, the real brand). */
  readonly label: string;
  readonly armorDark: string;
  readonly armorMid: string;
  readonly armorLit: string;
  readonly visor: string;
  readonly core: string;
  readonly emblem: Emblem;
  readonly health: number;
  readonly score: number;
}

/** Corridor Zero = Access / Zero Trust; roster evokes the ZTNA/SSE incumbents. */
export const CORRIDOR_ZERO_ARCHETYPES: readonly ArchetypeSpec[] = [
  {
    kind: "zeescaler",
    label: "Zeescaler",
    armorDark: "#0b2740",
    armorMid: "#134b74",
    armorLit: "#2aa3ff",
    visor: "#7fd0ff",
    core: "#2aa3ff",
    emblem: "ring",
    health: 55,
    score: 100,
  },
  {
    kind: "pale-alto",
    label: "Pale Alto",
    armorDark: "#2c1c0b",
    armorMid: "#5c3b17",
    armorLit: "#ff7a1a",
    visor: "#ffd39b",
    core: "#ff7a1a",
    emblem: "wedge",
    health: 70,
    score: 130,
  },
  {
    kind: "netscope",
    label: "NetScope",
    armorDark: "#08302c",
    armorMid: "#0f5f56",
    armorLit: "#17b3a3",
    visor: "#8ff0e4",
    core: "#17b3a3",
    emblem: "bars",
    health: 60,
    score: 110,
  },
  {
    kind: "sysko",
    label: "Sysko",
    armorDark: "#0a2233",
    armorMid: "#124a63",
    armorLit: "#1ba0e2",
    visor: "#a9e4ff",
    core: "#1ba0e2",
    emblem: "bridge",
    health: 80,
    score: 140,
  },
];

const W = 64;
const H = 96;

function frameFromDraw(draw: (ctx: CanvasRenderingContext2D) => void): SpriteFrame {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d unavailable for sprite bake");
  ctx.clearRect(0, 0, W, H);
  draw(ctx);
  const img = ctx.getImageData(0, 0, W, H);
  const data = new Uint32Array(W * H);
  data.set(new Uint32Array(img.data.buffer));
  return { width: W, height: H, data, anchorX: 0.5, anchorY: 1 };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawEmblem(ctx: CanvasRenderingContext2D, cx: number, cy: number, spec: ArchetypeSpec): void {
  ctx.save();
  ctx.strokeStyle = "#0a0d12";
  ctx.lineWidth = 2;
  ctx.fillStyle = "#0a0d12";
  switch (spec.emblem) {
    case "ring":
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, 2, 0, Math.PI * 2);
      ctx.fill();
      break;
    case "wedge":
      ctx.beginPath();
      ctx.moveTo(cx, cy - 6);
      ctx.lineTo(cx + 6, cy + 5);
      ctx.lineTo(cx - 6, cy + 5);
      ctx.closePath();
      ctx.fill();
      break;
    case "bars":
      ctx.fillRect(cx - 6, cy - 5, 3, 10);
      ctx.fillRect(cx - 1, cy - 5, 3, 10);
      ctx.fillRect(cx + 4, cy - 5, 3, 10);
      break;
    case "bridge":
      ctx.beginPath();
      ctx.arc(cx, cy + 3, 6, Math.PI, 0);
      ctx.stroke();
      ctx.fillRect(cx - 7, cy + 2, 14, 3);
      break;
  }
  ctx.restore();
}

function drawActor(
  ctx: CanvasRenderingContext2D,
  spec: ArchetypeSpec,
  legPhase: number,
  opts: { lean?: number; alpha?: number; sink?: number; flash?: boolean } = {},
): void {
  const lean = opts.lean ?? 0;
  const alpha = opts.alpha ?? 1;
  const sink = opts.sink ?? 0;
  const cx = W / 2;
  const baseY = H - 4 - sink;

  ctx.save();
  ctx.globalAlpha = alpha * 0.35;
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.ellipse(cx, H - 5, 20, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = alpha;

  ctx.translate(cx, baseY);
  ctx.rotate(lean);
  ctx.translate(-cx, -baseY);

  // Legs.
  const swing = Math.sin(legPhase) * 6;
  ctx.fillStyle = spec.armorDark;
  roundRect(ctx, cx - 15 + swing, baseY - 34, 12, 34, 5);
  ctx.fill();
  roundRect(ctx, cx + 3 - swing, baseY - 34, 12, 34, 5);
  ctx.fill();

  // Torso.
  const torsoTop = baseY - 66;
  ctx.fillStyle = spec.armorMid;
  roundRect(ctx, cx - 18, torsoTop, 36, 36, 9);
  ctx.fill();
  ctx.fillStyle = spec.armorLit;
  roundRect(ctx, cx - 18, torsoTop, 36, 10, 9);
  ctx.fill();

  // Shoulders.
  ctx.fillStyle = spec.armorDark;
  roundRect(ctx, cx - 24, torsoTop + 2, 10, 16, 4);
  ctx.fill();
  roundRect(ctx, cx + 14, torsoTop + 2, 10, 16, 4);
  ctx.fill();

  // Core glow + emblem.
  const glow = ctx.createRadialGradient(cx, torsoTop + 20, 1, cx, torsoTop + 20, 13);
  glow.addColorStop(0, spec.core);
  glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, torsoTop + 20, 13, 0, Math.PI * 2);
  ctx.fill();
  drawEmblem(ctx, cx, torsoTop + 20, spec);

  // Head + visor.
  ctx.fillStyle = spec.armorDark;
  roundRect(ctx, cx - 11, torsoTop - 20, 22, 22, 7);
  ctx.fill();
  ctx.fillStyle = spec.visor;
  ctx.shadowColor = spec.visor;
  ctx.shadowBlur = 8;
  roundRect(ctx, cx - 8, torsoTop - 13, 16, 6, 3);
  ctx.fill();
  ctx.shadowBlur = 0;

  // Weapon.
  ctx.fillStyle = "#10151d";
  roundRect(ctx, cx - 26, torsoTop + 22, 30, 7, 3);
  ctx.fill();
  ctx.fillStyle = spec.core;
  ctx.fillRect(cx - 27, torsoTop + 24, 3, 3);

  if (opts.flash) {
    ctx.globalAlpha = alpha * 0.5;
    ctx.fillStyle = "#ffffff";
    roundRect(ctx, cx - 18, torsoTop, 36, 36, 9);
    ctx.fill();
  }
  ctx.restore();
}

export function buildActorSprites(spec: ArchetypeSpec): ActorSprites {
  const walk = [0, 1, 2, 3].map((i) =>
    frameFromDraw((ctx) => drawActor(ctx, spec, (i / 4) * Math.PI * 2)),
  );
  const pain = frameFromDraw((ctx) => drawActor(ctx, spec, 0, { lean: -0.12, flash: true }));
  const die = [0, 1, 2, 3, 4].map((i) =>
    frameFromDraw((ctx) =>
      drawActor(ctx, spec, 0, { lean: -0.15 - i * 0.28, sink: i * 12, alpha: 1 - i * 0.16 }),
    ),
  );
  return { walk, pain, die };
}

/** Build the sprite set for each archetype, keyed by kind. Call lazily at first render. */
export function buildRoster(specs: readonly ArchetypeSpec[]): Map<string, ActorSprites> {
  const map = new Map<string, ActorSprites>();
  for (const spec of specs) map.set(spec.kind, buildActorSprites(spec));
  return map;
}
